import React from "react";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";

const DASHBOARD_TYPOGRAPHY = {
  sectionTitle: "text-xs font-semibold uppercase tracking-[0.18em] text-white/65",
  value: "text-2xl font-semibold tracking-tight text-white",
  body: "text-sm font-medium text-white",
  meta: "text-sm text-white/72",
};

const formatPriceValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "—";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCurrencyValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "—";
  return numeric.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatSignedCurrencyValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return `${numeric >= 0 ? "+" : "-"}${Math.abs(numeric).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * AccountSnapshot
 *
 * Props:
 *   account               - current account object (balance, buying_power, daily_pnl, …)
 *   selectedTradingAccount - the full account record for the selected trading account
 *   currentPrice          - latest market price
 *   priceChange           - % change (number)
 *   bidPrice              - current bid
 *   askPrice              - current ask
 *   dayHigh               - day high price
 *   dayLow                - day low price
 *   trend                 - "BULLISH" | "BEARISH"
 *   trendColor            - tailwind class for trend text colour
 *   trendBg               - tailwind class for trend badge background
 *   effectiveAccountBalance - resolved balance to display
 *   effectiveBuyingPower  - resolved buying power to display
 *   displayedDayPnl       - resolved day P&L to display
 *   selectedInstrumentOption - { label } for the selected instrument
 *   symbol                - current symbol string
 */
const AccountSnapshot = ({
  account,
  selectedTradingAccount,
  currentPrice,
  priceChange,
  bidPrice,
  askPrice,
  dayHigh,
  dayLow,
  trend,
  trendColor,
  trendBg,
  effectiveAccountBalance,
  effectiveBuyingPower,
  displayedDayPnl,
  realtimeUnrealizedPnl,
  selectedInstrumentOption,
  symbol,
}) => {
  return (
    <Card className="bg-white/5 border-white/10">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className={DASHBOARD_TYPOGRAPHY.sectionTitle}>Account Snapshot</div>
            <div className="truncate text-xs text-white/70">
              {account?.broker_display_name || selectedTradingAccount?.broker_display_name || "Connect a broker account"}
            </div>
          </div>
          <Badge variant="secondary" className="border-white/10 bg-white/5 text-white/70">
            {(account?.account_type || selectedTradingAccount?.account_type || "sim").toUpperCase()}
          </Badge>
        </div>
        <div className="flex justify-between items-start mb-2">
          <div>
            <div className={DASHBOARD_TYPOGRAPHY.value}>{formatPriceValue(currentPrice)}</div>
            <div className={`text-sm font-medium ${priceChange >= 0 ? "text-green-500" : "text-red-500"}`}>
              {priceChange >= 0 ? "+" : ""}{priceChange.toFixed(2)}%
            </div>
          </div>
          <Badge className={`${trendBg} ${trendColor} border-none`}>
            {trend}
          </Badge>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className={DASHBOARD_TYPOGRAPHY.meta}>Balance:</span>{" "}
            <span className={DASHBOARD_TYPOGRAPHY.body}>{formatCurrencyValue(effectiveAccountBalance)}</span>
          </div>
          <div>
            <span className={DASHBOARD_TYPOGRAPHY.meta}>Day P&L:</span>{" "}
            <span className={displayedDayPnl >= 0 ? "text-green-400" : "text-red-400"}>
              {formatSignedCurrencyValue(displayedDayPnl)}
            </span>
          </div>
          <div>
            <span className={DASHBOARD_TYPOGRAPHY.meta}>Unrealized:</span>{" "}
            <span className={realtimeUnrealizedPnl >= 0 ? "text-green-400" : "text-red-400"}>
              {formatSignedCurrencyValue(realtimeUnrealizedPnl)}
            </span>
          </div>
          <div>
            <span className={DASHBOARD_TYPOGRAPHY.meta}>Buying Power:</span>{" "}
            <span className={DASHBOARD_TYPOGRAPHY.body}>{formatCurrencyValue(effectiveBuyingPower)}</span>
          </div>
          <div>
            <span className={DASHBOARD_TYPOGRAPHY.meta}>Contract:</span>{" "}
            <span className={DASHBOARD_TYPOGRAPHY.body}>{selectedInstrumentOption?.label || symbol}</span>
          </div>
          <div>
            <span className={DASHBOARD_TYPOGRAPHY.meta}>Bid:</span>{" "}
            <span className={DASHBOARD_TYPOGRAPHY.body}>{formatPriceValue(bidPrice)}</span>
          </div>
          <div>
            <span className={DASHBOARD_TYPOGRAPHY.meta}>Ask:</span>{" "}
            <span className={DASHBOARD_TYPOGRAPHY.body}>{formatPriceValue(askPrice)}</span>
          </div>
          <div>
            <span className={DASHBOARD_TYPOGRAPHY.meta}>High:</span>{" "}
            <span className={DASHBOARD_TYPOGRAPHY.body}>{formatPriceValue(dayHigh)}</span>
          </div>
          <div>
            <span className={DASHBOARD_TYPOGRAPHY.meta}>Low:</span>{" "}
            <span className={DASHBOARD_TYPOGRAPHY.body}>{formatPriceValue(dayLow)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default React.memo(AccountSnapshot);
